import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../utils/authInterceptor';
import {
  useLocalParticipant,
  useRemoteParticipants,
  useRoomContext,
  useTracks,
  ConnectionState,
  RoomAudioRenderer,
  TrackToggle,
  ParticipantTile,
  AudioTrack,
  VideoTrack,
  RoomContext
} from '@livekit/components-react';
import { Room, RoomEvent, Track } from 'livekit-client';
// FriendUpgradeModal removed for FaceTime-style code simplicity
import CollaborationCelebration from './CollaborationCelebration';
import ToastNotification from './ToastNotification';
import FriendCollaborationMobile from './FriendCollaborationMobile';
import './FriendCollaborationInterface.css';
import './FriendCollaborationDesktop.css';

// Mobile device detection utility
const isMobileDevice = () => {
  // Check viewport width
  const isNarrowViewport = window.innerWidth < 768;

  // Check user agent for mobile devices
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Check for touch support
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Consider it mobile if narrow viewport OR (mobile UA AND touch)
  return isNarrowViewport || (isMobileUA && hasTouchScreen);
};

// Tappy Labs Learning Aquarium Branding
const TappyLabsBranding = {
  colors: {
    primary: {
      learningAquariumBlue: "#0EA5E9",
      confidenceFishGold: "#F59E0B", 
      deepOcean: "#0F172A",
      clarityWhite: "#FAFAFA",
    },
    secondary: {
      ecosystemGreen: "#10B981",
      crystalWaterBlue: "#E3F8FF",
      aquariumGlassClear: "#F0FDFF",
      livingCoral: "#FFE4E1",
    },
    semantic: {
      success: "#10B981",
      warning: "#F59E0B",
      error: "#EF4444",
      info: "#0EA5E9",
      neutral: "#64748B",
    },
    gradients: {
      aquariumDepth: "linear-gradient(135deg, #0EA5E9 0%, #0F172A 100%)",
      confidenceGlow: "linear-gradient(135deg, #F59E0B 0%, #F97316 100%)",
      crystalClear: "linear-gradient(135deg, #FAFAFA 0%, #F1F5F9 100%)",
      ecosystemFlow: "linear-gradient(135deg, #10B981 0%, #0EA5E9 100%)",
    }
  },
  components: {
    glassCard: {
      background: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      borderRadius: "16px",
      boxShadow: "0 8px 32px rgba(15, 23, 42, 0.1)",
    }
  }
};

// Connected Room Component with LiveKit hooks
const ConnectedRoomContent = ({
  problemContext,
  friendName,
  handleManualExit,

  // Multi-friend trust chain props
  userAuthState,
  currentUser,
  participants,
  canCurrentUserInvite,
  showInviteForm,
  setShowInviteForm,
  inviteFriendName,
  setInviteFriendName,
  invitationResult,
  isCreatingInvite,
  handleInviteMoreFriends,
  sessionStartTime
}) => {
  // LiveKit hooks - these will work because we're inside RoomContext
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  const audioTracks = useTracks([
    { source: Track.Source.Microphone, onlySubscribed: true }
  ]);
  const videoTracks = useTracks([
    { source: Track.Source.Camera, onlySubscribed: true }
  ]);
  
  // TrackToggle components handle microphone and camera state automatically

  return (
    <div style={{
      background: TappyLabsBranding.colors.gradients.crystalClear,
      minHeight: '100vh',
      padding: '20px',
      fontFamily: "'Inter', sans-serif"
    }}>
      {/* Silent session tracker - floating pill */}
      <div style={{
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '8px 16px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        fontSize: '14px',
        color: '#374151',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        {Math.floor((Date.now() - (sessionStartTime || Date.now())) / 60000)} min helping {friendName || 'friend'}
      </div>

      {/* Lab Participants View */}
      <div className="participants-section" style={{
        ...TappyLabsBranding.components.glassCard,
        background: TappyLabsBranding.colors.secondary.crystalWaterBlue,
        border: `2px solid ${TappyLabsBranding.colors.primary.learningAquariumBlue}`,
        marginBottom: '20px',
        padding: '20px'
      }}>
        <h3 style={{
          color: TappyLabsBranding.colors.primary.deepOcean,
          marginBottom: '16px',
          fontSize: '20px',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          🔬 Learning Lab Participants
        </h3>
        <div className="participants-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '16px'
        }}>
          
          {/* You (Lab Partner) */}
          <div className="participant-tile local-participant" style={{
            ...TappyLabsBranding.components.glassCard,
            background: TappyLabsBranding.colors.gradients.confidenceGlow,
            border: `2px solid ${TappyLabsBranding.colors.primary.confidenceFishGold}`,
            padding: '16px',
            color: 'white'
          }}>
            <div className="participant-info" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <span className="participant-name" style={{
                fontWeight: 600,
                fontSize: '16px'
              }}>
                🔬 You ({friendName}) - Lab Assistant
              </span>
              <span className="speaking-indicator" style={{ fontSize: '20px' }}>
                {localParticipant?.isSpeaking ? '🗣️' : '🐠'}
              </span>
            </div>
            {/* Video feed for local participant (friend) */}
            {videoTracks
              .filter(trackRef => trackRef.participant === localParticipant)
              .map((trackRef) => (
                <div key="local-video" className="participant-video" style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '2px solid rgba(255,255,255,0.3)'
                }}>
                  <VideoTrack trackRef={trackRef} />
                </div>
              ))
            }
          </div>

          {/* Remote Lab Participants (Student + Mira) */}
          {remoteParticipants.map((participant) => {
            // Find video track for this participant
            const participantVideoTrack = videoTracks.find(
              trackRef => trackRef.participant.identity === participant.identity
            );
            
            const isStudent = participant.identity.includes('user-');
            const isMira = !isStudent;
            
            return (
              <div key={participant.identity} className="participant-tile" style={{
                ...TappyLabsBranding.components.glassCard,
                background: isStudent ? 
                  TappyLabsBranding.colors.gradients.ecosystemFlow : 
                  TappyLabsBranding.colors.gradients.aquariumDepth,
                border: `2px solid ${isStudent ? 
                  TappyLabsBranding.colors.secondary.ecosystemGreen : 
                  TappyLabsBranding.colors.primary.learningAquariumBlue}`,
                padding: '16px',
                color: 'white'
              }}>
                <div className="participant-info" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '12px'
                }}>
                  <span className="participant-name" style={{
                    fontWeight: 600,
                    fontSize: '16px'
                  }}>
                    {isStudent ? '🧪 Your Friend (Student)' : '🌊 Mira (AI Lab Guide)'}
                  </span>
                  <span className="speaking-indicator" style={{ fontSize: '20px' }}>
                    {participant.isSpeaking ? '🗣️' : '🐠'}
                  </span>
                </div>
                {/* Video feed for remote participant if available */}
                {participantVideoTrack && (
                  <div className="participant-video" style={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '2px solid rgba(255,255,255,0.3)'
                  }}>
                    <VideoTrack trackRef={participantVideoTrack} />
                  </div>
                )}
                {/* Show lab avatar if no video */}
                {!participantVideoTrack && (
                  <div className="participant-avatar" style={{
                    height: '120px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '48px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    border: '2px dashed rgba(255,255,255,0.3)'
                  }}>
                    {isStudent ? '👤' : '🌊'}
                  </div>
                )}
              </div>
            );
          })}
          
          {/* Audio tracks for all remote participants */}
          {audioTracks
            .filter(trackRef => trackRef.participant !== localParticipant)
            .map((trackRef) => (
              <AudioTrack key={trackRef.participant.identity} trackRef={trackRef} />
            ))}
        </div>
      </div>

      {/* Lab Equipment Controls */}
      <div className="controls-section" style={{
        ...TappyLabsBranding.components.glassCard,
        background: TappyLabsBranding.colors.secondary.aquariumGlassClear,
        border: `2px solid ${TappyLabsBranding.colors.primary.learningAquariumBlue}`,
        padding: '20px',
        marginBottom: '20px',
        display: 'flex',
        gap: '16px',
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        <TrackToggle 
          source={Track.Source.Microphone}
          className="mic-toggle"
          showIcon={false}
          style={{
            background: TappyLabsBranding.colors.gradients.ecosystemFlow,
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            padding: '12px 20px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
            fontFamily: "'Inter', sans-serif"
          }}
        >
          🎤 Lab Microphone
        </TrackToggle>
        
        <TrackToggle 
          source={Track.Source.Camera}
          className="camera-toggle"
          showIcon={false}
          style={{
            background: TappyLabsBranding.colors.gradients.confidenceGlow,
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            padding: '12px 20px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(245, 158, 11, 0.3)',
            fontFamily: "'Inter', sans-serif"
          }}
        >
          🔬 Lab Camera
        </TrackToggle>
        
        <button
          onClick={handleManualExit}
          className="disconnect-button"
          title="Exit study session"
          style={{
            background: '#EF4444',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            padding: '12px 20px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(239, 68, 68, 0.3)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
        >
          End Session
        </button>
        
        {/* MULTI-FRIEND INVITATION INTERFACE */}
        <div className="multi-friend-section" style={{
          marginTop: '20px',
          padding: '16px',
          background: 'rgba(14, 165, 233, 0.05)',
          borderRadius: '12px',
          border: '1px solid rgba(14, 165, 233, 0.2)'
        }}>
          <h4 style={{
            margin: '0 0 12px 0',
            color: TappyLabsBranding.colors.primary.deepOcean,
            fontSize: '1rem',
            fontWeight: '600'
          }}>
            🐠 Invite More Friends
          </h4>
          
          {/* Show different UI based on user authentication state */}
          {userAuthState === 'authenticated' && canCurrentUserInvite ? (
            <>
              {!showInviteForm ? (
                <button
                  onClick={() => setShowInviteForm(true)}
                  style={{
                    background: TappyLabsBranding.colors.gradients.ecosystemFlow,
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                  }}
                >
                  🌊 Invite Another Friend
                </button>
              ) : (
                <div className="invite-form" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Friend's name"
                    value={inviteFriendName}
                    onChange={(e) => setInviteFriendName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleInviteMoreFriends(inviteFriendName)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid rgba(14, 165, 233, 0.3)',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    onClick={() => handleInviteMoreFriends(inviteFriendName)}
                    disabled={isCreatingInvite || !inviteFriendName.trim()}
                    style={{
                      background: TappyLabsBranding.colors.primary.learningAquariumBlue,
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      fontSize: '14px',
                      cursor: isCreatingInvite ? 'not-allowed' : 'pointer',
                      opacity: isCreatingInvite || !inviteFriendName.trim() ? 0.6 : 1
                    }}
                  >
                    {isCreatingInvite ? '...' : '📤'}
                  </button>
                  <button
                    onClick={() => {
                      setShowInviteForm(false);
                      setInviteFriendName('');
                    }}
                    style={{
                      background: 'transparent',
                      color: TappyLabsBranding.colors.semantic.neutral,
                      border: '1px solid rgba(100, 116, 139, 0.3)',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}
            </>
          ) : userAuthState === 'authenticated' && !canCurrentUserInvite ? (
            <p style={{ 
              fontSize: '14px', 
              color: TappyLabsBranding.colors.semantic.neutral,
              margin: 0 
            }}>
              Room is full or you've already invited a friend
            </p>
          ) : (
            // Guest user conversion prompt
            <div style={{
              padding: '12px',
              background: 'rgba(245, 158, 11, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(245, 158, 11, 0.3)'
            }}>
              <p style={{ 
                fontSize: '14px', 
                color: TappyLabsBranding.colors.primary.deepOcean,
                margin: '0 0 8px 0'
              }}>
                🔑 Sign up to invite more friends to this session!
              </p>
              <button
                onClick={() => window.location.href = '/signup?ref=invite_friends'}
                style={{
                  background: TappyLabsBranding.colors.primary.confidenceFishGold,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                🐠 Sign Up to Invite
              </button>
            </div>
          )}
          
          {/* Show invitation results */}
          {invitationResult && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              borderRadius: '8px',
              background: invitationResult.success 
                ? 'rgba(16, 185, 129, 0.1)' 
                : 'rgba(239, 68, 68, 0.1)',
              border: `1px solid ${invitationResult.success 
                ? 'rgba(16, 185, 129, 0.3)' 
                : 'rgba(239, 68, 68, 0.3)'}`
            }}>
              {invitationResult.success ? (
                <>
                  <p style={{ 
                    fontSize: '14px', 
                    color: TappyLabsBranding.colors.secondary.ecosystemGreen,
                    margin: '0 0 8px 0',
                    fontWeight: '600'
                  }}>
                    ✅ {invitationResult.message}
                  </p>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    padding: '8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    color: TappyLabsBranding.colors.primary.deepOcean
                  }}>
                    {invitationResult.shareUrl}
                  </div>
                </>
              ) : (
                <p style={{ 
                  fontSize: '14px', 
                  color: TappyLabsBranding.colors.semantic.error,
                  margin: 0 
                }}>
                  ❌ {invitationResult.error}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Study Buddy Guidelines - Accountability Focus */}
      <div className="help-tips" style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <h4 style={{
          fontSize: '17px',
          fontWeight: 600,
          marginBottom: '16px',
          color: '#111'
        }}>
          Your role as study buddy
        </h4>
        <ul style={{
          margin: 0,
          paddingLeft: '20px',
          lineHeight: 1.8,
          fontSize: '15px',
          color: '#374151'
        }}>
          <li>Keep them focused (AI will teach the concepts)</li>
          <li>Ask "do you get it?" after explanations</li>
          <li>Encourage them to try practice problems</li>
          <li>Celebrate when they figure it out</li>
        </ul>
        <div style={{
          marginTop: '16px',
          padding: '12px',
          background: '#F3F4F6',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#6B7280'
        }}>
          The AI teaches. You keep them from quitting.
        </div>
      </div>
    </div>
  );
};

// Inner component that uses LiveKit hooks inside RoomContext
const StudyRoomContent = ({ connectionState, problemContext, onHangUp, showToast }) => {
  const remoteParticipants = useRemoteParticipants();
  const { localParticipant } = useLocalParticipant();
  const videoTracks = useTracks([
    { source: Track.Source.Camera, onlySubscribed: false }  // Get all tracks including local
  ]);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(true);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true); // Default to speaker on for mobile

  // Detect if user is on mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Debug video tracks (only log when tracks actually change)
  useEffect(() => {
    // Video debug logging removed for production
  }, [videoTracks.length, localParticipant?.identity, localParticipant?.isCameraEnabled]);

  const toggleMute = async () => {
    if (localParticipant) {
      const currentlyEnabled = localParticipant.isMicrophoneEnabled;
      await localParticipant.setMicrophoneEnabled(!currentlyEnabled);
      setIsMuted(!currentlyEnabled);
    }
  };

  const toggleVideo = async () => {
    if (localParticipant) {
      try {
        const currentlyEnabled = localParticipant.isCameraEnabled;
        await localParticipant.setCameraEnabled(!currentlyEnabled);
        setIsVideoOff(currentlyEnabled); // If camera was enabled, now it's off (and vice versa)
      } catch (error) {
        console.error('Camera permission error:', error);
        // Permission denied - show friendly message
        if (error.name === 'NotAllowedError' || error.message.includes('Permission denied')) {
          showToast('📹 Camera access denied. Please allow camera access in your browser settings and try again.', 'warning');
        }
      }
    }
  };

  const toggleSpeaker = async () => {
    try {
      // Toggle speaker state
      const newSpeakerState = !isSpeakerOn;
      setIsSpeakerOn(newSpeakerState);

      // Get all audio elements in the page
      const audioElements = document.querySelectorAll('audio');

      if (audioElements.length === 0) {
        console.warn('No audio elements found');
        return;
      }

      // Change audio output for all audio elements
      for (const audio of audioElements) {
        if (typeof audio.setSinkId === 'function') {
          try {
            // Empty string = default device (speaker on mobile)
            // 'default' also works for speaker output
            await audio.setSinkId('');
          } catch (err) {
            console.error('setSinkId error:', err);
          }
        } else {
          console.warn('setSinkId not supported in this browser');
        }
      }
    } catch (error) {
      console.error('Speaker toggle error:', error);
    }
  };

  // 2025 State management - Discord + iOS features (MUST be before conditional returns)
  const [controlsVisible, setControlsVisible] = React.useState(true);
  const [connectionQuality, setConnectionQuality] = React.useState('good');
  const [mouseIdleTimer, setMouseIdleTimer] = React.useState(null);

  // Get screen share tracks (hook must be called unconditionally)
  const screenShareTracks = useTracks([
    { source: Track.Source.ScreenShare, onlySubscribed: false }
  ]);

  // Monitor connection quality
  React.useEffect(() => {
    if (!localParticipant) return;

    const updateQuality = () => {
      const quality = localParticipant.connectionQuality;
      if (quality === 'poor') setConnectionQuality('poor');
      else if (quality === 'lost') setConnectionQuality('bad');
      else setConnectionQuality('good');
    };

    updateQuality();
    localParticipant.on('connectionQualityChanged', updateQuality);
    return () => localParticipant.off('connectionQualityChanged', updateQuality);
  }, [localParticipant]);

  // Auto-hide controls like Discord/Zoom
  const handleMouseMove = () => {
    setControlsVisible(true);
    if (mouseIdleTimer) clearTimeout(mouseIdleTimer);
    const timer = setTimeout(() => setControlsVisible(false), 3000);
    setMouseIdleTimer(timer);
  };

  React.useEffect(() => {
    return () => {
      if (mouseIdleTimer) clearTimeout(mouseIdleTimer);
    };
  }, [mouseIdleTimer]);

  // Mode detection - intelligent layout
  const hasScreenShare = screenShareTracks.length > 0;
  const friendParticipant = remoteParticipants.find(p =>
    !p.identity.includes('mira-agent') && !p.identity.startsWith('agent-')
  );

  // Show connecting state
  if (connectionState !== 'connected') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #0EA5E9 0%, #10B981 100%)',
        color: 'white',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            border: '3px solid rgba(255, 255, 255, 0.4)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              border: '4px solid white',
              borderTopColor: 'transparent',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
          <h2 style={{ fontSize: '28px', fontWeight: '600', margin: 0 }}>Connecting to study session...</h2>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Connected - show 2025 Discord + iOS interface
  return (
    <div
      onMouseMove={handleMouseMove}
      style={{
        position: 'fixed',
        inset: 0,
        margin: 0,
        padding: 0,
        background: '#000',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        color: 'white',
        overflow: 'hidden'
      }}>
      {/* Connection quality indicator - iOS style */}
      {connectionQuality !== 'good' && controlsVisible && (
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 16px',
          borderRadius: '20px',
          background: connectionQuality === 'poor' ? 'rgba(251, 191, 36, 0.9)' : 'rgba(239, 68, 68, 0.9)',
          backdropFilter: 'blur(20px)',
          fontSize: '13px',
          fontWeight: '600',
          zIndex: 50,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          {connectionQuality === 'poor' ? '⚠️ Weak connection' : '❌ Connection issues'}
        </div>
      )}

      {/* Session info - Auto-hiding top bar */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        padding: '16px 24px',
        background: 'linear-gradient(to bottom, rgba(26, 26, 30, 0.95), transparent)',
        backdropFilter: 'blur(20px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        opacity: controlsVisible ? 1 : 0,
        transform: controlsVisible ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 40,
        pointerEvents: controlsVisible ? 'auto' : 'none'
      }}>
        <div>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#fff' }}>
            {problemContext || 'Study Session'}
          </div>
          <div style={{ fontSize: '13px', color: '#76909F', marginTop: '2px' }}>
            {remoteParticipants.length + 1} participants
          </div>
        </div>
        <div style={{
          background: 'rgba(41, 122, 146, 0.2)',
          padding: '6px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          color: '#42909E',
          border: '1px solid rgba(66, 144, 158, 0.3)'
        }}>
          {hasScreenShare ? '📺 Sharing' : '💬 Connected'}
        </div>
      </div>

      {/* Main content - Full screen immersive (FaceTime/Discord style) */}
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#000'
      }}>
        {/* Primary view - Screen share OR Host's video fills screen */}
        {(() => {
          const hostParticipant = remoteParticipants.find(p =>
            !p.identity.includes('mira-agent') && !p.identity.startsWith('agent-')
          );

          // Priority 1: Screen share (if available)
          const hostScreenShare = hostParticipant ? screenShareTracks.find(
            trackRef => trackRef.participant.identity === hostParticipant.identity
          ) : null;

          // Priority 2: Camera video (fallback)
          const hostVideoTrack = hostParticipant ? videoTracks.find(
            trackRef => trackRef.participant.identity === hostParticipant.identity
          ) : null;

          const hostIsSpeaking = hostParticipant?.isSpeaking || false;
          const isScreenSharing = !!hostScreenShare;

          return (
            <>
              {/* Main content - Screen share takes priority, then video */}
              <div style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#000'
              }}>
                {hostScreenShare ? (
                  // SCREEN SHARE MODE - Show homework/screen
                  <>
                    <VideoTrack
                      trackRef={hostScreenShare}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  </>
                ) : hostVideoTrack ? (
                  // CAMERA MODE - Show host's face
                  <>
                    <VideoTrack
                      trackRef={hostVideoTrack}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                    {/* Speaking indicator border for host */}
                    {hostIsSpeaking && (
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        border: '4px solid #10b981',
                        pointerEvents: 'none',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        zIndex: 1
                      }} />
                    )}
                  </>
                ) : (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '24px'
                  }}>
                    <div style={{
                      width: '160px',
                      height: '160px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #297A92 0%, #42909E 50%, #6DB4CD 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '64px',
                      fontWeight: '600',
                      color: 'white',
                      boxShadow: '0 12px 48px rgba(66, 144, 158, 0.4)'
                    }}>
                      {hostParticipant?.name?.charAt(0).toUpperCase() || 'H'}
                    </div>
                  </div>
                )}

                {/* Host name label */}
                <div style={{
                  position: 'absolute',
                  top: '24px',
                  left: '24px',
                  background: 'rgba(0, 0, 0, 0.75)',
                  backdropFilter: 'blur(10px)',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  zIndex: 20,
                  opacity: controlsVisible ? 1 : 0,
                  transition: 'opacity 0.3s'
                }}>
                  {hostIsSpeaking && <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#10b981',
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }} />}
                  Host
                </div>
              </div>

              {/* Your video - Floating PiP (FaceTime style) - Always visible */}
              {(() => {
                const myVideoTrack = videoTracks.find(trackRef => trackRef.participant === localParticipant);
                const cameraIsOn = localParticipant?.isCameraEnabled;
                const iAmSpeaking = localParticipant?.isSpeaking || false;

                return (
                  <div style={{
                    position: 'absolute',
                    bottom: '120px',
                    right: '24px',
                    width: '280px',
                    height: '210px',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    background: '#1c1c1e',
                    border: iAmSpeaking ? '3px solid #10b981' : '3px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: iAmSpeaking
                      ? '0 0 0 4px rgba(16, 185, 129, 0.3), 0 8px 32px rgba(0, 0, 0, 0.4)'
                      : '0 8px 32px rgba(0, 0, 0, 0.4)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: 30,
                    opacity: controlsVisible ? 1 : 0.7
                  }}>
                    {myVideoTrack && cameraIsOn ? (
                      <VideoTrack
                        trackRef={myVideoTrack}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#1c1c1e'
                      }}>
                        <div style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #297A92 0%, #42909E 50%, #6DB4CD 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '32px',
                          fontWeight: '600',
                          color: 'white',
                          boxShadow: '0 8px 32px rgba(66, 144, 158, 0.35)'
                        }}>
                          {localParticipant?.name ? localParticipant.name.charAt(0).toUpperCase() : 'Y'}
                        </div>
                      </div>
                    )}
                    <div style={{
                      position: 'absolute',
                      bottom: '8px',
                      left: '8px',
                      background: 'rgba(0, 0, 0, 0.75)',
                      backdropFilter: 'blur(10px)',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'white'
                    }}>
                      You {isMuted ? '(Muted)' : ''}
                    </div>
                  </div>
                );
              })()}
            </>
          );
        })()}
      </div>

      {/* Bottom - Auto-hiding toolbar (Discord/Zoom style) */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '24px',
        background: 'linear-gradient(to top, rgba(26, 26, 30, 0.95), transparent)',
        backdropFilter: 'blur(20px)',
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        opacity: controlsVisible ? 1 : 0,
        transform: controlsVisible ? 'translateY(0)' : 'translateY(100%)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 40,
        pointerEvents: controlsVisible ? 'auto' : 'none'
      }}>
        {/* Mute button */}
        <button
          onClick={toggleMute}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            border: 'none',
            background: isMuted ? '#ef4444' : '#297A92',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: isMuted
              ? '0 0 20px rgba(239, 68, 68, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3)'
              : '0 0 20px rgba(41, 122, 146, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3)'
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            {isMuted ? (
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
            ) : (
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z"/>
            )}
          </svg>
        </button>

        {/* Video button */}
        <button
          onClick={toggleVideo}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            border: 'none',
            background: isVideoOff ? '#64748B' : '#297A92',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            {isVideoOff ? (
              <path d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z"/>
            ) : (
              <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
            )}
          </svg>
        </button>

        {/* Speaker button - only show on mobile */}
        {isMobile && (
          <button
            onClick={toggleSpeaker}
            style={{
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              border: 'none',
              background: isSpeakerOn ? 'rgba(41, 122, 146, 0.9)' : '#64748B',
              color: 'white',
              fontSize: '32px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              {isSpeakerOn ? (
                // Speaker on - with sound waves
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
              ) : (
                // Speaker off - no waves
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
              )}
            </svg>
          </button>
        )}

        {/* Hang up button */}
        <button
          onClick={onHangUp}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            border: 'none',
            background: '#ef4444',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 0 20px rgba(239, 68, 68, 0.5), 0 4px 12px rgba(0, 0, 0, 0.3)'
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
          </svg>
        </button>
      </div>

      <RoomAudioRenderer />
    </div>
  );
};

const FriendCollaborationInterface = () => {
  // Get invite token from URL parameters
  const { invite_token } = useParams();

  // Mobile detection - Check once at mount and on resize
  const [isMobile, setIsMobile] = useState(isMobileDevice());

  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [room] = useState(() => new Room());
  const [connectionState, setConnectionState] = useState('disconnected');
  const [sessionData, setSessionData] = useState(null);
  const [invitationData, setInvitationData] = useState(null);
  const [friendName, setFriendName] = useState('');
  const [problemContext, setProblemContext] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [showConversionPrompt, setShowConversionPrompt] = useState(false);
  const [showIdentityVerification, setShowIdentityVerification] = useState(true);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [sessionCount, setSessionCount] = useState(0);
  const [coveredByHostSubscription, setCoveredByHostSubscription] = useState(false);
  
  // Smart user state detection for contextual experience
  const [userAuthState, setUserAuthState] = useState('unknown'); // 'guest', 'logged_out', 'authenticated', 'unknown'
  const [currentUser, setCurrentUser] = useState(null);
  const [conversionStrategy, setConversionStrategy] = useState('guest');

  // Multi-friend trust chain state
  const [participants, setParticipants] = useState([]);
  const [invitationChain, setInvitationChain] = useState([]);
  const [canCurrentUserInvite, setCanCurrentUserInvite] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteFriendName, setInviteFriendName] = useState('');
  const [invitationResult, setInvitationResult] = useState(null);
  const [isCreatingInvite, setIsCreatingInvite] = useState(false);

  // Friend upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Collaboration celebration state
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationRewards, setCelebrationRewards] = useState(null);
  const [isHelper, setIsHelper] = useState(false);

  // StudyBuddyPrompt: Host info for friend-side connection (Snapchat model)
  const [hostInfo, setHostInfo] = useState(null);

  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });

  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: '', type: 'info' });
  };

  // Smart user state detection - runs immediately without blocking UX
  useEffect(() => {
    const detectUserState = async () => {
      try {
        // Try to get current user without requiring authentication
        const response = await axios.get('/api/auth/verify');

        const data = response.data;
        if (data.valid && data.user) {
          // User is authenticated
          setUserAuthState('authenticated');
          setCurrentUser(data.user);
          setConversionStrategy('authenticated');
        } else {
          // User has account but not logged in
          setUserAuthState('logged_out');
          setConversionStrategy('returning_user');
        }
      } catch (error) {
        // Network error or no previous account - treat as guest
        setUserAuthState('guest');
        setConversionStrategy('guest');
      }
    };

    // Run detection but don't block the collaboration experience
    detectUserState();

    // NOTE: sessionStartTime is now set in initializeFriendSession when connection succeeds
    // This ensures we only track ACTUAL collaboration time, not page load time
    // Gen Z UX: Don't show "Great session! 0 minutes" for failed connections

    // Get session count from localStorage
    const storedCount = parseInt(localStorage.getItem('morpheus_help_session_count') || '0');
    setSessionCount(storedCount);
  }, []);


  // Load friend join data using secure token from URL path parameter
  useEffect(() => {
    const loadFriendJoinData = async () => {
      // Use invite_token from URL path (e.g., /join/abc123)
      if (!invite_token) {
        setError('No session token found. Please use a valid invitation link.');
        return;
      }

      // Get anonymous ID using hybrid approach (PostHog + localStorage fallback)
      let anonymousId = null;
      try {
        // Try PostHog distinct_id first (most reliable)
        anonymousId = window.posthog?.get_distinct_id();
      } catch (e) {
        console.warn('PostHog not available:', e);
      }

      // Fallback to localStorage pattern (matches TutorInterface)
      if (!anonymousId) {
        anonymousId = localStorage.getItem('morpheus_anon_id');
        if (!anonymousId) {
          // Generate new ID following existing pattern
          anonymousId = `anon-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
          localStorage.setItem('morpheus_anon_id', anonymousId);
        }
      }

      try {
        // Validate token with backend and send anonymous ID for session tracking
        const response = await axios.post('/api/validate-friend-session', {
          session_token: invite_token,
          anonymous_id: anonymousId  // Track sessions by this ID
        });

        const data = response.data;

        if (!data.success) {
          setError(data.error || 'Invalid session');
          return;
        }

        // Set invitation data for UI display
        setInvitationData({
          friend_name: 'Friend',
          problem_context: `${data.student_name}'s ${data.subject} session`
        });
        setFriendName('Friend');
        setProblemContext(`${data.student_name}'s ${data.subject} session`);

        // Check if host subscription covers this guest (FaceTime model - no paywall)
        if (data.covered_by_host_subscription) {
          setCoveredByHostSubscription(true);
        }

        // StudyBuddyPrompt: Capture host info for post-session connection prompt
        if (data.host_user_id) {
          setHostInfo({
            user_id: data.host_user_id,
            name: data.student_name || 'Friend',
            avatar_initial: (data.student_name || 'F')[0].toUpperCase()
          });
        }

        // Store session data with LiveKit credentials
        setSessionData({
          ws_url: data.livekit_url,
          token: data.access_token,
          room_name: data.room_name
        });

        // Skip identity verification and auto-connect
        setShowIdentityVerification(false);

        // Auto-initialize connection
        await initializeFriendSession(data);

      } catch (err) {
        console.error('Error validating friend session:', err);

        // Check if this is a session limit error (403 with session_limit_reached)
        // Gen Z UX: Clear message, not fake "Great session!" for something that never happened
        if (err.response?.status === 403 && err.response?.data?.error === 'session_limit_reached') {
          setSessionCount(err.response.data.session_count || 2);
          // Show upgrade modal instead of celebration (they never studied, don't say "Great session!")
          setShowUpgradeModal(true);
          return;
        }

        // Only show error if we don't have sessionData (i.e., first call failed)
        if (!sessionData) {
          setError(err.response?.data?.message || err.response?.data?.error || 'Invalid session. Please try entering the code again.');
        }
      }
    };

    loadFriendJoinData();
  }, [invite_token]);

  // Initialize friend session with 4-digit code data
  const initializeFriendSession = async (joinData) => {
    try {
      setIsConnecting(true);
      setError(null);

      console.log('🎯 Friend connecting to room:', joinData.room_name);

      // Connect to LiveKit room with token from 4-digit code
      await room.connect(joinData.livekit_url, joinData.access_token);
      setConnectionState('connected');

      // Mark session start time for duration tracking
      setSessionStartTime(Date.now());
      console.log('🎯 Friend connected successfully, session started');

      // Increment session count using functional setState (production-ready approach)
      setSessionCount(prevCount => {
        const newCount = prevCount + 1;
        localStorage.setItem('morpheus_help_session_count', newCount.toString());
        return newCount;
      });

    } catch (err) {
      console.error('🎯 Failed to connect to study session:', err);
      // Gen Z UX: Clear, actionable error message
      // Common case: Host already left the session
      setError('Your friend may have ended the session. Ask them to share a new code!');
      setConnectionState('disconnected');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleIdentityConfirmation = () => {
    // No longer needed for 4-digit code system, but kept for compatibility
    setShowIdentityVerification(false);
  };

  // Trigger minimal celebration with accountability focus
  // Gen Z principle: Reward ONLY after real collaboration (no fake "Great session!" for 0 minutes)
  const triggerSuccessCelebration = () => {
    const sessionDuration = sessionStartTime ? Math.floor((Date.now() - sessionStartTime) / 1000) : 0;

    // Don't show celebration if session was less than 30 seconds (no real collaboration)
    // This prevents the confusing "Great session! 0 minutes" when connection fails
    if (sessionDuration < 30) {
      console.log('🎯 Skipping celebration - session too short:', sessionDuration, 'seconds');
      // Just redirect home instead of showing fake celebration
      window.location.href = '/';
      return;
    }

    setShowCelebration(true);
  };

  // Mark collaboration as successful (for conversion timing)
  const markCollaborationSuccess = () => {
    setHadSuccessfulCollaboration(true);
  };

  // Handle manual exit (triggers celebration/conversion)
  const handleManualExit = () => {
    markCollaborationSuccess();
    if (room && connectionState === 'connected') {
      room.disconnect();
    }
    // Celebration will trigger via useEffect when disconnected
  };


  // Track collaboration success for conversion timing
  const [hadSuccessfulCollaboration, setHadSuccessfulCollaboration] = useState(false);

  // Handle room events
  useEffect(() => {
    if (!room) return;

    const handleConnected = () => {
      setConnectionState('connected');
    };

    const handleDisconnected = () => {
      setConnectionState('disconnected');
    };

    const handleReconnecting = () => {
      setConnectionState('reconnecting');
    };

    // Handle data messages from host (e.g., session ended)
    const handleDataReceived = (payload, participant) => {
      try {
        const decoder = new TextDecoder();
        const message = decoder.decode(payload);
        const data = JSON.parse(message);

        // Host ended the collaboration session
        if (data.type === 'collaboration_ended' && data.ended_by === 'host') {
          // Mark as successful and disconnect
          markCollaborationSuccess();
          if (room && room.state === 'connected') {
            room.disconnect();
          }
          // Celebration modal will trigger via the disconnected useEffect
        }
      } catch (err) {
        console.error('Error handling data message:', err);
      }
    };

    room.on(RoomEvent.Connected, handleConnected);
    room.on(RoomEvent.Disconnected, handleDisconnected);
    room.on(RoomEvent.Reconnecting, handleReconnecting);
    room.on(RoomEvent.DataReceived, handleDataReceived);

    return () => {
      room.off(RoomEvent.Connected, handleConnected);
      room.off(RoomEvent.Disconnected, handleDisconnected);
      room.off(RoomEvent.Reconnecting, handleReconnecting);
      room.off(RoomEvent.DataReceived, handleDataReceived);
    };
  }, [room]);

  // Production conversion trigger: After natural session end
  useEffect(() => {
    if (connectionState === 'disconnected' && hadSuccessfulCollaboration) {
      // Brief delay to let users process session ending
      const conversionTimer = setTimeout(() => {
        triggerSuccessCelebration();
      }, 1000); // 1 second delay for natural transition

      return () => clearTimeout(conversionTimer);
    }
  }, [connectionState, hadSuccessfulCollaboration]);


  // Multi-friend trust chain invitation functions
  const checkCanCurrentUserInvite = () => {
    // Only authenticated users can invite additional friends (trust chain rule)
    if (userAuthState !== 'authenticated' || !currentUser) {
      return false;
    }
    
    // Check if room is at capacity (max 4 participants)
    if (participants.length >= 4) {
      return false;
    }
    
    // In a real implementation, we'd check if user has already invited someone
    // For now, allow if authenticated
    return true;
  };

  const handleInviteMoreFriends = async (friendName) => {
    if (!friendName || !friendName.trim()) {
      setInvitationResult({ success: false, error: 'Please enter a friend\'s name' });
      return;
    }

    setIsCreatingInvite(true);
    
    try {
      // Use the room name from session data for the API call
      const roomName = sessionData?.room_name || invitationData?.room_name;
      if (!roomName) {
        throw new Error('Room information not available');
      }

      const response = await axios.post(`/api/voice/invite-additional-friend/${roomName}`, {
        friend_name: friendName.trim()
      });

      if (response.data.success) {
        setInvitationResult({
          success: true,
          friendName: friendName.trim(),
          shareUrl: response.data.invitation_url,
          message: `Invitation created! Share this link with ${friendName.trim()}:`
        });
        
        // Reset form
        setInviteFriendName('');
        setShowInviteForm(false);
        
        // Auto-clear success message after 10 seconds
        setTimeout(() => {
          setInvitationResult(null);
        }, 10000);
        
      } else {
        throw new Error(response.data.error || 'Failed to create invitation');
      }
      
    } catch (error) {
      console.error('Error creating additional invitation:', error);
      setInvitationResult({
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to create invitation'
      });
      
      // Auto-clear error message after 5 seconds
      setTimeout(() => {
        setInvitationResult(null);
      }, 5000);
    } finally {
      setIsCreatingInvite(false);
    }
  };

  // Update participant tracking when room participants change
  useEffect(() => {
    // This would be called when LiveKit participants change
    // For now, we'll update the invitation capability
    setCanCurrentUserInvite(checkCanCurrentUserInvite());
  }, [userAuthState, currentUser, participants]);

  if (error) {
    return (
      <div className="friend-interface-error">
        <div className="error-content">
          <h2>😔 Unable to Join Session</h2>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="retry-button"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (isConnecting) {
    return (
      <div className="friend-interface-loading">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <h2>🎧 Connecting to Study Session...</h2>
          <p>Getting you ready to help your friend!</p>
        </div>
      </div>
    );
  }

  // Simple study room layout matching MD file spec
  return (
    <>
      <RoomContext.Provider value={room}>
        {isMobile ? (
          // Mobile-first friend interface (portrait-optimized for phones)
          <FriendCollaborationMobile
            problemContext={problemContext}
            friendName={friendName}
            handleManualExit={handleManualExit}
            sessionStartTime={sessionStartTime}
          />
        ) : (
          // Desktop/tablet friend interface
          <StudyRoomContent connectionState={connectionState} problemContext={problemContext} onHangUp={handleManualExit} showToast={showToast} />
        )}
      </RoomContext.Provider>

      {/* Minimal Celebration Modal */}
      {showCelebration && (
        <CollaborationCelebration
          isVisible={showCelebration}
          onClose={() => {
            setShowCelebration(false);
            // Redirect to landing page after celebration
            window.location.href = '/';
          }}
          sessionDuration={sessionStartTime ? Math.floor((Date.now() - sessionStartTime) / 1000) : 0}
          friendName={friendName || 'your friend'}
          focusMultiplier={3}
          sessionCount={sessionCount}
          roomToken={invite_token}
          coveredByHostSubscription={coveredByHostSubscription}
          // StudyBuddyPrompt props (Snapchat model - friend sees HOST as studyPartner)
          studyPartner={hostInfo}
          isAuthenticated={userAuthState === 'authenticated'}
          sessionId={sessionData?.room_name}
        />
      )}

      {/* Toast Notification */}
      {toast.show && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
          show={toast.show}
        />
      )}
    </>
  );
};

export default FriendCollaborationInterface;